import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormGroup, UntypedFormControl, Validators } from "@angular/forms";
import { ADPResourceService, CustomFormControl, ListItem, Resource } from 'digital-core';
//import { Address, GooglePlaceDirective } from "src/app/core/components/ngx-google-places-autocomplete";
import { DynamicScriptLoaderService } from "src/app/core/services/dynamic-script-loader-service";
import { SanitizeService } from "src/app/core/services/sanitize.service";
import PlaceResult = google.maps.places.PlaceResult;
import { AddressValidationService } from "src/app/core/services/address-validation.service";
import { MatDialog } from "@angular/material/dialog";
import { AddressValidationModalWindowComponent } from "src/app/shared/components/address-validation-modal-window/address-validation-modal-window.component";
import { ValidationAddress } from "src/app/model/account/address-validation-response.dto"
import { ContractContactDetails } from "src/app/model/account/user-otp-request";
import { GoogleAnalyticsService } from "src/app/core/services/google-tag-manager.service";
import { GTMOnboardingEvents, OnboardingSteps } from "src/app/model/google-tag-manager/api-status";

@Component({
  selector: 'app-contact-setup',
  templateUrl: './contact-setup.component.html',
  styleUrls: ['./contact-setup.component.scss'],
})
export class ContactSetupComponent implements OnInit {
  @Input() contactInfo: ContractContactDetails;
  @Input() primaryEmail: string;
  @Output() submitted = new EventEmitter();

  addressResourceInfo: Resource;
  validationError: string;
  states: ListItem[];
  selectedState: { Key, Value };
  isGoogleMapsApiLoaded = false;
  showPhoneConsent = false;
  showPhoneNumber = false;
  isSPOEEnabled = false;

  googlePlacesOptions = {
    types: [],
    componentRestrictions: {
      country: 'US'
    }
  };

  validationForm = new FormGroup({
    Phone: new CustomFormControl(),
    Address1: new CustomFormControl(),
    Address2: new CustomFormControl(),
    City: new CustomFormControl(),
    State: new CustomFormControl(),
    ZipCode: new CustomFormControl(),
    FullAddress: new CustomFormControl(),
    Consent: new UntypedFormControl('', [Validators.requiredTrue]),
  });

  constructor(
    public resourceService: ADPResourceService,
    private sanitizeService: SanitizeService,
    private scriptLoaderService: DynamicScriptLoaderService,
    private addressValidationService: AddressValidationService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    this.showPhoneConsent = this.resourceService.isPortalSettingTrue('Signup_Create_Account_ShowPhoneConsent');
    this.showPhoneNumber = this.resourceService.isPortalSettingTrue('Signup_Create_Account_ShowPhoneNumber');
    if (this.isSPOEEnabled) {
      this.showPhoneNumber = true;
    }
    if (!this.showPhoneConsent) {
      this.validationForm.get('Consent').reset();
      this.validationForm.get('Consent').clearValidators();
    }
    this.loadStates(this.contactInfo.state);
    this.validationForm.get('Phone').setValue(this.contactInfo.phone);
    this.validationForm.get('Address1').setValue(this.contactInfo.addressLine1);
    this.validationForm.get('Address2').setValue(this.contactInfo.addressLine2);
    this.validationForm.get('City').setValue(this.contactInfo.city);
    this.validationForm.get('ZipCode').setValue(this.contactInfo.zipCode?.substr(0,5));
    this.addressResourceInfo = this.getResource('Signup_Create_Account_Address1_Field');
    this.scriptLoaderService.loadScript('google-maps-api', '').then(x => {
      this.isGoogleMapsApiLoaded = window.hasOwnProperty('google');
    });
  }

  sanitizeInput(inputValue) {
    inputValue.target.value = this.sanitizeService.sanitizeInput(inputValue.target.value);
  }

  private getResource(resoureKey: string): Resource {
    return this.resourceService.getResourceObject(resoureKey);
  }

  onAddressMapped($event: PlaceResult) {
    // to do - move to model file
    let streetNumber;
    let streetName = '';
    let postalCode;
    let city;
    let state;
    $event.address_components.forEach(value => {
      if (value.types.indexOf('street_number') > -1) {
        streetNumber = Number(value.short_name);
      }
      if (value.types.indexOf('route') > -1) {
        streetName = value.long_name;
      }
      if (value.types.indexOf('postal_code') > -1) {
        postalCode = Number(value.short_name);
      }
      if (value.types.indexOf('locality') > -1 || value.types.indexOf('sublocality_level_1') > -1) {
        city = value.long_name;
      }
      if (value.types.indexOf('administrative_area_level_1') > -1) {
        state = value.short_name;
      }
    });

    let addressLine1 = typeof streetNumber === 'undefined' ? streetName : streetNumber + ' ' + streetName;
    this.validationForm.get('Address1').setValue(addressLine1);
    if (Object.keys(city).length !== 0) {
      this.validationForm.get('City').setValue(city);
    }
    else {
      this.validationForm.get('City').setValue(null);
    }
    this.validationForm.get('State').setValue(state);
    if (postalCode !== undefined) {
      this.validationForm.get('ZipCode').setValue(`${postalCode}`.length === 4 ? '0' + `${postalCode}` : `${postalCode}`);
    }
    else {
      this.validationForm.get('ZipCode').setValue(null);
      this.validationForm.controls.ZipCode.markAsTouched();
    }
  }
  /*
  This function is no longer in use; as this function used with
  another library that is no longer supported with latest
  version of Angular.
  Keeping it here for reference for now.
  */
  handleAddressChange(address: any) {
    this.validationForm.get("FullAddress").setValue(address.formatted_address);
    if (address) {
      let streetNumber = address.address_components.find((x) =>
        x.types.includes("street_number")
      );
      let street = address.address_components.find((x) =>
        x.types.includes("route")
      );
      let address2Comp = address.address_components.find((x) =>
        x.types.includes("")
      );
      // TODO: determine why the address3 code was commented out and see if we should leave it commented out, bring it back (uncomment it), or delete it.
      // let address3Comp;
      // if (!this.resourceService.getResourceObject('Address3', 'Field').Hidden) {
      //   address3Comp = address.address_components.find((x) =>
      //     x.types.includes("locality")
      //   );
      // }

      if (!address2Comp) {
        address2Comp = address.address_components.find((x) =>
          x.types.includes("sublocality")
        );
      }

      let streetNum = streetNumber ? streetNumber.long_name : "";
      let streetLogname = street ? street.long_name : "";

      let address1 = (`${streetNum} ${streetLogname}`).trim();
      let address2 = address2Comp ? address2Comp.long_name : "";
      // let address3 = address3Comp ? address3Comp.long_name : "";

      this.validationForm.get("Address1").setValue(address1);
      this.validationForm.get("Address2").setValue(address2);
      // this.validationForm.get("Address3").setValue(address3);

      setTimeout(() => {
        this.validationForm.get("Address1").updateValueAndValidity();
      }, 1);
      setTimeout(() => {
        this.validationForm.get("Address2").updateValueAndValidity();
      }, 1);
      setTimeout(() => {
        // this.validationForm.get("Address3").updateValueAndValidity();
      }, 1);
      let postalCode = address.address_components.find((x) =>
        x.types.includes("postal_code")
      );

      this.validationForm
        .get("ZipCode")
        .setValue(postalCode ? postalCode.long_name : "");
      setTimeout(() => {
        this.validationForm.get("ZipCode").updateValueAndValidity();
      }, 1);

      this.setGoogleAutoCompleteCity(address);

      let province = address.address_components.find((x) =>
        x.types.includes("administrative_area_level_1")
      );
      if (this.states) {
        this.selectedState = this.states.find(
          (x) => x.Key === province.short_name
        );
      }
      if (!this.selectedState) {
        this.validationForm.get("State").setValue(null);
      } else {
        this.validationForm.get("State").setValue(this.selectedState.Key);
        setTimeout(() => {
          this.validationForm.get("State").updateValueAndValidity();
        }, 1);
      }
    }
    this.validationForm.updateValueAndValidity();
  }

  setGoogleAutoCompleteCity(address: any) {
    let city = this.resourceService.getPortalSetting("Global_GoogleCityField_" + this.googlePlacesOptions.componentRestrictions.country)?.Value
      ? address.address_components.find((x) =>
        x.types.includes(this.resourceService.getPortalSetting("Global_GoogleCityField_" + this.googlePlacesOptions.componentRestrictions.country).Value)
      )
      : address.address_components.find((x) =>
        x.types.includes(this.resourceService.getPortalSetting("Global_GoogleCityField").Value)
      )

    let _city = city
      ? city.long_name
      : address.address_components.find((x) =>
        x.types.includes("administrative_area_level_2")
      )
        ? address.address_components.find((x) =>
          x.types.includes("administrative_area_level_2")
        ).long_name
        : "";

    this.validationForm.get("City").setValue(city ? city.long_name : _city);
    setTimeout(() => {
      this.validationForm.get("City").updateValueAndValidity();
    }, 1);
  }

  loadStates(stateName: string) {
    let stateList = this.resourceService.getListItemResource(
      "Global " +
      this.resourceService.getPortalSetting("Global_Country").Value +
      " States"
    );
    if (stateList) {
      stateList.subscribe(
        (response) => {
          if (response) {
            this.states = response.sort((obj1, obj2) => {
              let a = obj1.Value.trim()
                .replace(/&nbsp;/g, "")
                .toLowerCase();
              let b = obj2.Value.trim()
                .replace(/&nbsp;/g, "")
                .toLowerCase();
              return a.localeCompare(b);
            });
            this.states.splice(0, 0, new ListItem('', null, null));
            if (stateName) {
              this.selectedState = this.states.find(x => x.Value === stateName || x.Key === stateName);
              this.validationForm.get('State').setValue(this.selectedState.Key);
            } else {
              this.selectedState = new ListItem('', "", "");
            }
          }
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      this.states = [];
    }
  }

  submitForm() {
    this.contactInfo.phone = this.validationForm.get('Phone').value;
    this.contactInfo.addressLine1 = this.validationForm.get('Address1').value;
    this.contactInfo.addressLine2 = this.validationForm.get('Address2').value;
    this.contactInfo.city = this.validationForm.get('City').value;
    this.contactInfo.state = this.validationForm.get('State').value;
    this.contactInfo.zipCode = this.validationForm.get('ZipCode').value;
    this.contactInfo.smsConsent = this.validationForm.get('Consent').value ?? false;
    this.validateAddress();
  }

  validateAddress() {
    this.addressValidationService.validateAddress(this.contactInfo).subscribe(resp => {
      if (Object.keys(resp).length == 0) {
        this.showAddressValidationModal(null);
      }
      // if the statusCode is populated pull the message from SF and display
      else if (resp.formattedAddress.statusCode && resp.formattedAddress.statusCode.length > 0) {
        this.showAddressValidationModal(resp.formattedAddress.statusCode);
      }
      // if the recordType = PostOfficeBox pull the message from SF and display
      else if (resp.formattedAddress.recordType && resp.formattedAddress.recordType === 'PostOfficeBox') {
        this.showAddressValidationModal('PostOfficeBox');
      }
      // if their is no statusCode and the confidence is less than 80%
      // show the user the suggested correct address and ask to confirm.
      else if (resp.actualConfidencePercentage < 80) {
        this.showAddressValidationModal('Confidence', resp.formattedAddress);
      }
      // if their is no statusCode and the confidence is 80 or greater then
      // Save formatted address and go next
      else {
        this.updateAddress(resp.formattedAddress);
      }
    })
  }

  showAddressValidationModal(statusCode: string, formattedAddress: ValidationAddress = null) {
    let address: string = null;
    if (formattedAddress) {
      address = `${formattedAddress.addressLine1}<br/>${formattedAddress.city}<br/>${formattedAddress.state} ${formattedAddress.postalCode}`;
    }
    const addressDialog = this.dialog.open(AddressValidationModalWindowComponent, {
      data: {
        title: 'Signup_Address_Validation_Code_' + (statusCode ?? 'UnableToValidate'),
        body: address,
        cancelButtonLabel: 'Global_Button_Back',
        nextButtonLabel: (formattedAddress ? 'Global_Button_Save' : null)
      }
    });

    addressDialog.afterClosed().subscribe(resp => {
      if (resp === 'Yes') {
        this.updateAddress(formattedAddress);
      }
    })
  }

  updateAddress(formattedAddress: ValidationAddress) {
    this.contactInfo.addressLine1 = formattedAddress.addressLine1;
    this.contactInfo.city = formattedAddress.city;
    this.contactInfo.state = formattedAddress.state;
    this.contactInfo.zipCode = formattedAddress.postalCode.substr(0,5);

    let payload = JSON.parse(JSON.stringify(this.contactInfo));
    payload.primaryEmail = this.primaryEmail; // Unmodified email address to search the contract in API
    this.addressValidationService.saveAddress(payload).subscribe({
      next: (resp) => {
      },
      error: (err) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
      }
    });
    this.submitted.emit(this.contactInfo);
  }
}
